import { HTMLInputTypeAttribute } from 'react'

export interface ConfigurationState {
  config: StateConfig
}

export enum DocumentType {
  PRODUCT = 'product',
  POLICY = 'policy',
  STATIC = 'static',
}

export interface Document {
  id: string
  type: DocumentType
  coverCode?: string
}

export interface ProductDocument extends Document {
  type: DocumentType.PRODUCT
  url: string
}

export interface PolicyDocument extends Document {
  type: DocumentType.POLICY
}

export interface StaticDocument extends Document {
  type: DocumentType.STATIC
}

export enum PromoType {
  NO_CODE = 'NO_CODE',
  RETAILER_CODE = 'RETAILER_CODE',
  RETAILER_AND_PROPOSAL_CODE = 'RETAILER_AND_PROPOSAL_CODE',
}

export interface PromoQuestionConfig {
  validation: string
  placeholder: string
  endpoint: string
  inputType?: HTMLInputTypeAttribute
}

export interface PromoConfig {
  inputs: InputConfig[]
}

export interface PromoNoCodeConfig extends PromoConfig {
  type: PromoType.NO_CODE
}

export interface PromoRetailerConfig extends PromoConfig {
  type: PromoType.RETAILER_CODE
  retailer: PromoQuestionConfig
}

export interface PromoRetailerAndProposalConfig extends PromoConfig {
  type: PromoType.RETAILER_AND_PROPOSAL_CODE
  retailer: PromoQuestionConfig
  proposalCode: PromoQuestionConfig
}

export type Provider = string

interface StripeConfig {
  apiKey: string
  stripeAccount: string | undefined
}

interface TwilioConfig {
  chatServer: string
}

export interface Localisation {
  language: string
  fallbackLanguage: string
  currency: string
  timeZone: string
}

/* eslint-disable  @typescript-eslint/no-explicit-any */
export interface Config {
  localisation?: Partial<Localisation>
  sessionClient: any
  apiClient: any
  app: any
  auth0: any
  state: any
  stripe: StripeConfig
  airbrake: any // Actually AirbrakeConfig
  logLevel: string
  twilio?: Partial<TwilioConfig>
}

/* eslint-enable  @typescript-eslint/no-explicit-any */

export interface AppConfig {
  assetDomain: string
  cookieDomain: string
  auth0Domain: string
  provider: Provider
  title: string
  description: string
  themeColor: string
}

export interface Scheme {
  schemeCode: string
  productUrlName: string
  hidden?: boolean
}

export interface FeatureToggles {
  // Long-running toggles
  renderCheckoutAction: boolean
  allowModifyBillingDay: boolean
  isAdmin: boolean
  multiQuote: boolean
  localisationSwitcher: boolean
  disableBind: boolean
  hidePromotionBanner: boolean
  axitechFnol: boolean
  newFeedback: boolean
}

export interface StateConfig {
  keys?: {
    maps?: string
  }
  productConfig: Record<string, ProductConfig>
  contentBaseUrl: string
  provider: Provider
  schemes: Scheme[]
  features: Partial<FeatureToggles>
  inputs: Record<string, InputConfig>
  adjustments: Record<string, ChangeConfig>
  account?: InputConfig[]
  proposalTracking?: string[]
  createProposal?: {
    lookups?: Record<string, string>
    passthroughs?: string[]
  }
  quoteData?: string[]
  backBooks?: string[]
}

export enum ProductKind {
  METERED = 'METERED',
  SUBSCRIPTION = 'SUBSCRIPTION',
  ANNUAL = 'ANNUAL',
  DRIVEAWAY = 'DRIVEAWAY',
}

export interface InputValueCondition {
  name: string
  isDefined?: boolean
  values?: unknown[]
}

export interface FeatureToggleCondition {
  featureToggle: keyof FeatureToggles
}

export interface AnonymousCondition {
  isAnonymousOnly: boolean
}

export interface PolicyCondition {
  isPolicy: boolean
}

export interface SchemeCodeCondition {
  schemeCodes: string[]
}

export interface AdminCondition {
  isAdmin: boolean
}

export interface AdminPermissionCondition {
  default?: boolean
  hasPermissions?: string[]
  notHasPermissions?: string[]
}

export interface EditClaimsCondition {
  canEditClaims: boolean
}

export interface PolicyStartedCondition {
  hasPolicyStarted: boolean
}

export type InputCondition =
  | InputValueCondition
  | FeatureToggleCondition
  | AnonymousCondition
  | PolicyCondition
  | AdminCondition
  | EditClaimsCondition

export type ProductCondition = PolicyStartedCondition | InputValueCondition

export type DisclosureSectionCondition = AnonymousCondition

/* eslint-disable  @typescript-eslint/no-explicit-any */
export interface InputConfig<TMeta = any | never> {
  name: string
  type: string
  meta?: TMeta
  validation?: Record<string, any>
  readOnly?: {
    conditions: InputCondition[]
  }
  conditional?: {
    preserve?: boolean
    conditions: InputCondition[]
  }
  options?: {
    hideFormHeader?: boolean
    helpLink?: boolean
    subheader?: boolean
    header?: boolean
  }
}

export type AdjustmentInputConfig<
  TChange extends ChangeConfig = ChangeConfig,
  TMeta = any | never,
> = InputConfig<TMeta> & {
  adjustment: TChange
}

export type AdjustmentUpdate = string | { id: string; path: string }[]

export enum ChangeType {
  ASSET_DISCLOSURE = 'ASSET_DISCLOSURE',
  PROFILE_DISCLOSURE = 'PROFILE_DISCLOSURE',
  QUOTE_DISCLOSURE = 'QUOTE_DISCLOSURE',
  START_DATE_CHANGE = 'START_DATE_CHANGE',
}

export interface QuoteChangeConfig {
  changeType: ChangeType.QUOTE_DISCLOSURE
  update: AdjustmentUpdate
}

export interface ProfileChangeConfig {
  changeType: ChangeType.PROFILE_DISCLOSURE
  update: AdjustmentUpdate
}

export interface AssetChangeConfig {
  changeType: ChangeType.ASSET_DISCLOSURE
  update: AdjustmentUpdate
}

export interface StartDateChangeConfig {
  changeType: ChangeType.START_DATE_CHANGE
}

export type ChangeConfig =
  | ProfileChangeConfig
  | AssetChangeConfig
  | QuoteChangeConfig
  | StartDateChangeConfig

export enum ManagementType {
  ADDITIONAL_PROFILE = 'ADDITIONAL_PROFILE',
  POLICYHOLDER = 'POLICYHOLDER',
}

export interface MainDisclosureConfig {
  id: string
  type: undefined
  inputs: AdjustmentInputConfig[]
  footerText?: string
  conditions?: DisclosureSectionCondition[]
}

export interface PolicyholderDisclosureConfig {
  id: string
  type: ManagementType.POLICYHOLDER
  inputs: InputConfig[]
  footerText?: string
  conditions?: DisclosureSectionCondition[]
}

export interface AdditionalProfileDisclosureConfig {
  id: string
  type: ManagementType.ADDITIONAL_PROFILE
  maxAdditionalProfiles: number
  inputs: AdjustmentInputConfig[]
  footerText?: string
  conditions?: DisclosureSectionCondition[]
}

export type DisclosureConfig =
  | MainDisclosureConfig
  | AdditionalProfileDisclosureConfig
  | PolicyholderDisclosureConfig

export interface ProposalConfirmationConfig {
  conditions: ProductCondition[]
  inputs: AdjustmentInputConfig[]
}

export interface QuoteManagementConfig {
  id: string
  type: undefined
  inputs: AdjustmentInputConfig[]
  conditions?: ProductCondition[]
}

export interface AdditionalProfileManagementConfig {
  id: string
  type: ManagementType.ADDITIONAL_PROFILE
  maxAdditionalProfiles: number
  inputs: AdjustmentInputConfig[]
  conditions: ProductCondition[]
}

export interface FeedbackConfig {
  type: string
  actions: Record<string, string>
}

export type ManagementConfig = QuoteManagementConfig | AdditionalProfileManagementConfig

export interface ProductConfig {
  productKind: ProductKind
  promo: Record<
    string,
    PromoRetailerConfig | PromoRetailerAndProposalConfig | PromoNoCodeConfig
  >
  assetTypes: AssetType[]
  excess: {
    voluntaryExcess: number[]
  }
  documents: Document[]
  howItWorks?: HowItWorksSection[]
  cancellationReasons: Record<string, string>
  checkoutChecklist?: string[]
  startAtDayDelay?: number
  disclosures: DisclosureConfig[]
  proposalConfirmation?: ProposalConfirmationConfig
  feedback?: Record<string, FeedbackConfig>
  management: ManagementConfig[]
  summary: ManagementConfig[]
  renewal: ManagementConfig[]
  accountSetup: AdjustmentInputConfig[]
  expiredProposal: AdjustmentInputConfig[]
  beforeWeBegin: AdjustmentInputConfig[]
  typeformSurveyId?: string
  hideReferralReasons?: boolean
}

export enum IncludedCoverType {
  STATIC = 'STATIC',
  ENDORSEMENT = 'ENDORSEMENT',
}

export interface IncludedCoverConfig {
  code: string
  type: IncludedCoverType
  active?: boolean
}

export interface OptionalCoverConfig {
  code: string
}

export interface AssetType {
  code: string
  core?: boolean
  covers: {
    included: IncludedCoverConfig[]
    optional?: OptionalCoverConfig[]
  }
}

/* eslint-disable  @typescript-eslint/no-explicit-any */
export interface Condition {
  path: string
  values: any[]
}

/* eslint-enable  @typescript-eslint/no-explicit-any */

export type ConditionParentNode = [string, ConditionNode, ConditionNode]
export type ConditionLeafNode = Condition
export type ConditionNode = ConditionParentNode | ConditionLeafNode

export interface HowItWorksSection {
  id: string
  illusId: string
}
